import Link from 'next/link'
import { Icon } from '@/components/homepage-2026/canvas/sprite'

/**
 * The compare tool's zero-products state — and, deliberately, the Suspense
 * fallback the static prerender bakes into the served HTML (FRIDGE-243).
 *
 * One component, two duties, kept identical on purpose: what a crawler reads
 * in the artifact is exactly what a visitor with no ?products selection sees
 * after hydration, so the served page never claims content the tool does not
 * show. The browse links are the crawlable path onward — a fallback of `null`
 * here is how this page served head + chrome + nothing to every non-JS
 * client.
 *
 * FRIDGE-872 — rebuilt in the homepage-2026 design language (canvas.css,
 * scoped under `.hp26`, which app/compare/page.tsx puts on <main>). The grey
 * `Scale` glyph and the three generic grey buttons are gone: the card now
 * reads as Fridge.com, the browse links are the canvas `.chip` pill, and the
 * real product rail with its Compare ticks renders underneath it (the picks
 * rail in compare-client.tsx) so a shopper can build a comparison from this
 * page instead of being sent away to find one.
 *
 * No props, on purpose: tests/compare-prerender-shell.test.ts pins the
 * boundary as `fallback={<CompareEmptyState />}` and the client's zero-state
 * as `<CompareEmptyState />`, which is what keeps the two views identical.
 */
export function CompareEmptyState() {
  return (
    <div
      style={{
        background: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: 14,
        padding: 'clamp(20px, 3vw, 32px)',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          alignSelf: 'flex-start',
          fontSize: 11,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: '#ff9951',
        }}
      >
        <Icon id="i-compare" className="i" />
        Nothing selected yet
      </span>

      <h2 className="sect-h" style={{ margin: 0 }}>
        {/* "three steps", not "three taps": the list below is three numbered
            steps, and step 1 alone asks for up to four ticks. */}
        Build a side-by-side in three steps
      </h2>

      <p className="sect-sub" style={{ margin: 0, maxWidth: 720, lineHeight: 1.55 }}>
        Pick up to four refrigerators or freezers and Fridge.com lines them up on one screen — the
        price each retailer is asking today, capacity, dimensions, energy use, color and ENERGY
        STAR certification, with the best number in every row marked. Tick <strong>Compare</strong> on
        any product below, on a product page, or anywhere else on the site, and it lands here.
      </p>

      <ol
        style={{
          margin: 0,
          padding: 0,
          listStyle: 'none',
          display: 'grid',
          gap: 12,
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))',
        }}
      >
        {[
          ['1', 'Tick Compare', 'Every product card on Fridge.com carries a Compare tick. Tick up to four.'],
          ['2', 'Line them up', 'Your picks appear as full product cards with the retailer ladder underneath each one.'],
          // Name only rows the table actually highlights. "the top rating" was
          // here until the Rating row came out — no product in the catalog
          // carries a rating, so it could never have been highlighted.
          ['3', 'Read the winner', 'The lowest price, the most retailers tracked, the largest capacity and the lowest energy use are highlighted in orange.'],
        ].map(([n, title, body]) => (
          <li
            key={n}
            style={{
              display: 'flex',
              gap: 10,
              alignItems: 'flex-start',
              background: '#f9fafb',
              border: '1px solid #eef1f5',
              borderRadius: 12,
              padding: 14,
            }}
          >
            <span
              style={{
                flex: 'none',
                width: 24,
                height: 24,
                borderRadius: 9999,
                background: '#ff9951',
                color: '#101828',
                fontSize: 12,
                fontWeight: 800,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              aria-hidden="true"
            >
              {n}
            </span>
            <span style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
              <span style={{ fontSize: 13.5, fontWeight: 700, color: '#101828' }}>{title}</span>
              <span style={{ fontSize: 12.5, color: '#6a7282', lineHeight: 1.45 }}>{body}</span>
            </span>
          </li>
        ))}
      </ol>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, paddingTop: 2 }}>
        <Link className="chip" href="/collections/refrigerators">
          Browse refrigerators
        </Link>
        <Link className="chip" href="/collections/freezers">
          Browse freezers
        </Link>
        <Link className="chip" href="/collections/french-door-refrigerators">
          French door refrigerators
        </Link>
        <Link className="chip" href="/collections/all">
          The whole catalog
        </Link>
      </div>
    </div>
  )
}
